import { useCallback, useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MediaLicenseModal — Before/After Photo Media License administration.
 *
 * Generates court-grade e-sign license PDFs owners countersign to grant
 * Moovin Forward permission to use before/after captures for portfolio,
 * grant applications, banker packets, and marketing. Every signed record
 * is SHA-256 fingerprinted (Corporate Resolute Framework Clause 1).
 */
const MediaLicenseModal = ({ open, onClose }) => {
  const [tab, setTab] = useState("queue");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/media-sharing`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setRows(d.agreements || []);
      } else setErr(`Could not load (HTTP ${r.status})`);
    } catch { setErr("Network error."); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  return (
    <div data-testid="media-license-backdrop" onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(2,8,20,0.78)",
                  display: "flex", justifyContent: "center", alignItems: "flex-start",
                  padding: "3rem 1.5rem", zIndex: 9998, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="media-license-modal"
           style={{ background: "linear-gradient(160deg, #0a1929 0%, #1b2a4e 100%)",
                    border: "1px solid rgba(184,145,90,0.5)", borderRadius: 10,
                    maxWidth: 960, width: "100%", padding: "1.8rem 2rem 1.6rem",
                    color: "#F5EFE1", boxShadow: "0 40px 80px rgba(0,0,0,0.5)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                      marginBottom: "1.1rem", flexWrap: "wrap", gap: "0.7rem" }}>
          <div>
            <div style={{ color: "#B8915A", fontSize: "0.7rem", letterSpacing: "0.22em",
                          textTransform: "uppercase", fontWeight: 800 }}>
              Media License · Owner E-Sign
            </div>
            <h2 style={{ margin: "0.3rem 0 0", fontSize: "1.4rem", fontWeight: 700 }}>
              Before / After Photo Portfolio Grants
            </h2>
            <div style={{ color: "#ADBED1", fontSize: "0.85rem", marginTop: 4 }}>
              Court-grade e-sign PDF. Tamper-evident SHA-256 on every signed record.
            </div>
          </div>
          <button type="button" data-testid="ml-close" onClick={onClose}
                  style={{ background: "transparent", color: "#B8915A", border: "1px solid #B8915A",
                           padding: "0.45rem 0.9rem", borderRadius: 4, fontSize: "0.8rem", fontWeight: 700, cursor: "pointer" }}>
            Close ✕
          </button>
        </div>

        <div style={{ display: "flex", gap: "0.4rem", borderBottom: "1px solid rgba(184,145,90,0.3)", marginBottom: "1rem" }}>
          {[
            { k: "queue",    label: `Signed Queue (${rows.length})`, testid: "ml-tab-queue" },
            { k: "generate", label: "Generate New", testid: "ml-tab-generate" },
          ].map((t) => (
            <button key={t.k} type="button" data-testid={t.testid} onClick={() => setTab(t.k)}
                    style={{ background: tab === t.k ? "rgba(184,145,90,0.2)" : "transparent",
                             color: tab === t.k ? "#F5EFE1" : "#B8915A", border: "none",
                             borderBottom: tab === t.k ? "2px solid #B8915A" : "2px solid transparent",
                             padding: "0.55rem 1rem", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer" }}>
              {t.label}
            </button>
          ))}
        </div>

        {err && <div data-testid="ml-error" style={{ background: "rgba(220,38,38,0.14)", border: "1px solid rgba(220,38,38,0.4)", color: "#FCA5A5", padding: "0.6rem 0.9rem", borderRadius: 5, marginBottom: "0.9rem" }}>⚠ {err}</div>}
        {loading && <div style={{ color: "#ADBED1", padding: "1rem 0" }}>Loading…</div>}

        {tab === "queue" && !loading && <QueueList rows={rows} />}
        {tab === "generate" && <GenerateForm onSaved={() => { setTab("queue"); load(); }} />}
      </div>
    </div>
  );
};


const QueueList = ({ rows }) => {
  if (!rows || rows.length === 0) {
    return <div data-testid="ml-empty" style={{ color: "#ADBED1", padding: "1.5rem 0.5rem", textAlign: "center" }}>
      No licenses yet. Generate one to send to an owner.
    </div>;
  }
  return (
    <div data-testid="ml-queue" style={{ display: "grid", gap: "0.55rem", maxHeight: "58vh", overflowY: "auto" }}>
      {rows.map((r) => {
        const signed = r.status === "signed";
        const color  = signed ? "#00DC78" : "#FFB347";
        const signUrl = `${BACKEND_URL}/api/media-sharing/${r.agreement_id}`;
        const pdfUrl  = `${BACKEND_URL}/api/media-sharing/${r.agreement_id}/pdf`;
        return (
          <div key={r.agreement_id} data-testid={`ml-row-${r.agreement_id}`}
               style={{ background: "rgba(0,0,0,0.22)", padding: "0.75rem 0.9rem",
                        borderRadius: 5, borderLeft: `3px solid ${color}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: "#F3F4F6", fontSize: "0.92rem", fontWeight: 700 }}>
                  {r.owner_legal_name}
                  {r.entity_name && <span style={{ color: "#ADBED1", fontWeight: 500, fontSize: "0.82rem" }}> · {r.entity_name}</span>}
                </div>
                <div style={{ color: "#ADBED1", fontSize: "0.78rem", marginTop: 2 }}>
                  {r.property_address}
                </div>
                <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: 3 }}>
                  {signed ? `✓ Signed ${r.signed_at?.split("T")[0]}` : "Awaiting signature"} · v{r.license_version}
                </div>
                {signed && r.signed_sha256 && (
                  <div style={{ color: "#5AC8D8", fontSize: "0.65rem", marginTop: 2, fontFamily: "monospace", wordBreak: "break-all" }}>
                    SHA-256: {r.signed_sha256}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid={`ml-pdf-${r.agreement_id}`}
                   style={{ background: "transparent", color: "#5AC8D8", border: "1px solid #5AC8D8",
                            padding: "0.35rem 0.75rem", borderRadius: 4, fontSize: "0.72rem", fontWeight: 700, textDecoration: "none", textAlign: "center" }}>
                  📄 View PDF
                </a>
                {!signed && (
                  <button type="button" data-testid={`ml-copy-${r.agreement_id}`}
                          onClick={() => { navigator.clipboard.writeText(signUrl); alert("Sign URL copied. Send to owner."); }}
                          style={{ background: "transparent", color: "#B8915A", border: "1px solid #B8915A",
                                   padding: "0.35rem 0.75rem", borderRadius: 4, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}>
                    🔗 Copy Sign URL
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};


const GenerateForm = ({ onSaved }) => {
  const [f, setF] = useState({ property_address: "", owner_legal_name: "", owner_email: "", entity_name: "", term_years: 0 });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async () => {
    if (!f.property_address.trim() || !f.owner_legal_name.trim()) {
      setErr("Property address and owner legal name are required."); return;
    }
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/media-sharing/generate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...f, term_years: Number(f.term_years) || 0 }),
      });
      if (r.ok) onSaved();
      else {
        const d = await r.json().catch(() => ({}));
        setErr(d.detail || `HTTP ${r.status}`);
      }
    } catch { setErr("Network error."); }
    setBusy(false);
  };

  const inp = (k, ph, type = "text") => (
    <input data-testid={`ml-gen-${k}`} type={type} value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph}
           style={{ width: "100%", background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
                    border: "1px solid rgba(184,145,90,0.3)", borderRadius: 4,
                    padding: "0.55rem 0.75rem", fontSize: "0.88rem" }} />
  );
  const lbl = (t) => <div style={{ color: "#B8915A", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 3 }}>{t}</div>;

  return (
    <div data-testid="ml-generate-form" style={{ display: "grid", gap: "0.85rem", maxHeight: "60vh", overflowY: "auto" }}>
      {err && <div style={{ background: "rgba(220,38,38,0.14)", border: "1px solid rgba(220,38,38,0.4)", color: "#FCA5A5", padding: "0.6rem 0.9rem", borderRadius: 5 }}>⚠ {err}</div>}
      <div>{lbl("Property Address *")}{inp("property_address", "1234 Main St, Arlington, TX 76001")}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.7rem" }}>
        <div>{lbl("Owner Legal Name *")}{inp("owner_legal_name", "Jane Doe")}</div>
        <div>{lbl("Owner Email")}{inp("owner_email", "owner@example.com", "email")}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "0.7rem" }}>
        <div>{lbl("Entity / LLC")}{inp("entity_name", "Doe Holdings LLC (optional)")}</div>
        <div>{lbl("Term (years · 0=perpetual)")}{inp("term_years", "0", "number")}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.4rem" }}>
        <button type="button" data-testid="ml-gen-submit" onClick={submit} disabled={busy}
                style={{ background: "linear-gradient(90deg, #B8915A, #8B6B3F)", color: "#F5EFE1",
                         border: "none", padding: "0.6rem 1.4rem", borderRadius: 5, fontSize: "0.9rem", fontWeight: 800,
                         cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
          {busy ? "Generating…" : "📄 Generate License PDF"}
        </button>
      </div>
    </div>
  );
};


export default MediaLicenseModal;
